import React, { useEffect, useState } from 'react';
import { format, formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  Avatar,
  Button,
  Card,
  Divider,
  Input,
  message,
  Progress,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { MdAdd, MdCheckCircle, MdCancel, MdAccessTime } from 'react-icons/md';
import { FaApple, FaGoogle, FaEnvelope, FaRoute } from 'react-icons/fa';
import { Link, useHistory } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../services/api';

import { Container } from './styles';

interface IUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  googleId?: string;
  appleId?: string;
  hasAnsweredAreasWorkSheet?: boolean;
  hasActivePlan?: boolean;
  lastAccessAt?: string;
  journey?: {
    completeDays: number[];
  };
}

const JOURNEY_TOTAL = 21;

const Users: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [usersList, setUsersList] = useState<IUser[]>([]);

  const history = useHistory();

  useEffect(() => {
    async function loadData(): Promise<void> {
      setLoading(true);

      try {
        const response = await api.get('/users-payment');

        const users = response.data;
        setUsersList(users);
      } catch (err) {
        message.error('Falha ao carregar dados');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  if (loading) {
    return (
      <Layout title="Usuários">
        <Spin style={{ marginTop: '50%' }} size="large" />
      </Layout>
    );
  }

  const getInitials = (user: IUser): string =>
    `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

  const getAvatarColor = (user: IUser): string => {
    const colors = [
      '#048ba8',
      '#2e4057',
      '#f18f01',
      '#c73e1d',
      '#3b1f2b',
      '#44bba4',
    ];
    const index =
      (user.firstName.charCodeAt(0) + user.lastName.charCodeAt(0)) %
      colors.length;
    return colors[index];
  };

  return (
    <Layout title="Usuários">
      <Container>
        <h2>Gerenciamento de Usuários</h2>

        <div id="controls">
          <Button
            icon={<MdAdd />}
            size="large"
            onClick={() => history.push('/users/add')}
          >
            Adicionar
          </Button>

          <Input.Search
            placeholder="Pesquisar"
            size="large"
            style={{ maxWidth: 290 }}
          />
        </div>

        <div className="users-grid">
          {usersList.map((user: IUser) => {
            const completedDays = user.journey?.completeDays.length || 0;
            const journeyPercent = Math.round(
              (completedDays / JOURNEY_TOTAL) * 100,
            );

            return (
              <Link to={`/users/${user._id}`} key={user._id}>
                <Card className="card" style={{ width: 300 }}>
                  {/* Header: avatar + nome + email + plano */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <Avatar
                      size={48}
                      style={{
                        backgroundColor: getAvatarColor(user),
                        fontWeight: 700,
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(user)}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 15,
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.firstName} {user.lastName}
                      </div>
                      <div
                        style={{
                          color: '#888',
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.email}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {user.hasActivePlan ? (
                          <Tag color="success" style={{ fontSize: 11 }}>
                            Plano ativo
                          </Tag>
                        ) : (
                          <Tag color="default" style={{ fontSize: 11 }}>
                            Sem plano
                          </Tag>
                        )}
                      </div>
                    </div>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Autenticação + planilha */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 4,
                    }}
                  >
                    <Tag
                      icon={
                        <FaEnvelope
                          style={{ marginRight: 4, verticalAlign: 'middle' }}
                        />
                      }
                      color="green"
                    >
                      Email
                    </Tag>
                    {user.appleId && (
                      <Tag
                        icon={
                          <FaApple
                            style={{ marginRight: 4, verticalAlign: 'middle' }}
                          />
                        }
                        color="default"
                      >
                        Apple
                      </Tag>
                    )}
                    {user.googleId && (
                      <Tag
                        icon={
                          <FaGoogle
                            style={{ marginRight: 4, verticalAlign: 'middle' }}
                          />
                        }
                        color="blue"
                      >
                        Google
                      </Tag>
                    )}
                    <Tooltip
                      title={
                        user.hasAnsweredAreasWorkSheet
                          ? 'Planilha da vida preenchida'
                          : 'Planilha da vida não preenchida'
                      }
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 12,
                          color: user.hasAnsweredAreasWorkSheet
                            ? '#52c41a'
                            : '#ff4d4f',
                          cursor: 'default',
                        }}
                      >
                        {user.hasAnsweredAreasWorkSheet ? (
                          <MdCheckCircle size={16} />
                        ) : (
                          <MdCancel size={16} />
                        )}
                        Planilha
                      </span>
                    </Tooltip>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Jornada */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 6,
                        fontSize: 13,
                      }}
                    >
                      <FaRoute size={14} color="#048ba8" />
                      <span style={{ color: '#555' }}>Jornada</span>
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontWeight: 600,
                          color: '#333',
                        }}
                      >
                        {completedDays} / {JOURNEY_TOTAL} dias
                      </span>
                    </div>
                    <Progress
                      percent={journeyPercent}
                      size="small"
                      strokeColor={
                        journeyPercent === 100 ? '#52c41a' : '#048ba8'
                      }
                      showInfo={false}
                    />
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  {/* Último acesso */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      fontSize: 12,
                      color: '#555',
                    }}
                  >
                    <MdAccessTime
                      size={15}
                      style={{ marginTop: 1, flexShrink: 0, color: '#048ba8' }}
                    />
                    {user.lastAccessAt ? (
                      <Tooltip
                        title={formatDistance(
                          new Date(user.lastAccessAt),
                          new Date(),
                          {
                            addSuffix: true,
                            locale: ptBR,
                          },
                        )}
                      >
                        <span>
                          <span style={{ color: '#888' }}>Último acesso</span>
                          <br />
                          <strong style={{ color: '#333' }}>
                            {format(
                              new Date(user.lastAccessAt),
                              'dd/MM/yyyy HH:mm',
                            )}
                          </strong>
                        </span>
                      </Tooltip>
                    ) : (
                      <span style={{ color: '#bbb', fontStyle: 'italic' }}>
                        Sem registro de acesso
                      </span>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </Container>
    </Layout>
  );
};

export default Users;
